import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ExternalLink, CheckCircle2 } from 'lucide-react';
import { emailsAPI } from '../../api/api';
import useEsMovil from '../../hooks/useEsMovil';
import { revisarNovedades } from '../guia/novedades';

// Caja ámbar del modal de factura: las notas para administración del
// presupuesto y, debajo, los correos "para administración" que nadie ha
// gestionado (incidente 2306 del 21-oct-2026: el cliente mandó en copia los
// datos de otra razón social y se facturó sin verlos).
// - Se monta al abrir el modal y pide GET /emails/para-administracion.
// - No bloquea la emisión. Si la petición falla, no se pinta ningún correo.
// - "Gestionado" deja el correo resuelto en el ERP (lo mismo que Listo en el
//   panel de IAmail): no toca el buzón real de nadie.

const fechaCorta = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
};

export default function AvisoAdministracionFactura({ presupuestoId, notas }) {
  const navigate = useNavigate();
  const esMovil = useEsMovil();
  const [correos, setCorreos] = useState([]);
  const [total, setTotal] = useState(0);
  const [marcando, setMarcando] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let vivo = true;
    setCorreos([]);
    setTotal(0);
    if (!presupuestoId) return undefined;
    emailsAPI.paraAdministracion(presupuestoId)
      .then(({ data }) => {
        if (!vivo) return;
        const lista = Array.isArray(data?.correos) ? data.correos : [];
        setCorreos(lista);
        setTotal(Math.max(Number(data?.total) || 0, lista.length));
      })
      .catch(() => {
        // Sin aviso de correos: la factura se puede emitir igual
      });
    return () => { vivo = false; };
  }, [presupuestoId]);

  const hayCorreos = correos.length > 0;
  // El modal no cambia la ruta: que se mire la novedad cuando sale el bloque.
  useEffect(() => {
    if (hayCorreos) revisarNovedades();
  }, [hayCorreos]);

  const abrir = (id) => {
    const ruta = `/emails?email=${encodeURIComponent(id)}`;
    if (esMovil) {
      navigate(ruta);
    } else {
      window.open(ruta, '_blank', 'noopener,noreferrer');
    }
  };

  const marcarGestionado = async (id) => {
    setMarcando(id);
    setError('');
    try {
      await emailsAPI.actualizar(id, { estado_gestion: 'resuelto' });
      setCorreos((prev) => prev.filter((c) => c.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      setError('No se pudo marcar como gestionado');
    } finally {
      setMarcando(null);
    }
  };

  const hayNotas = Boolean(notas && String(notas).trim() !== '');
  if (!hayNotas && !hayCorreos) return null;

  return (
    <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1 space-y-3">
          {hayNotas && (
            <div>
              <p className="text-xs font-bold text-amber-800 uppercase tracking-wide mb-1">
                Notas para administración
              </p>
              <p className="text-sm text-amber-900 whitespace-pre-wrap">{notas}</p>
            </div>
          )}
          {hayCorreos && (
            <div data-guia="factura-correos-administracion" data-testid="factura-correos-administracion">
              <p className="text-xs font-bold text-amber-800 uppercase tracking-wide mb-1">
                Correos para administración sin gestionar ({total})
              </p>
              <p className="text-sm text-amber-900 mb-2">
                Revisa estos correos antes de emitir: pueden cambiar a quién o cómo se factura.
              </p>
              <ul className="space-y-1.5">
                {correos.map((c) => {
                  const quien = c.remitente_nombre || c.remitente || 'Sin remitente';
                  const fecha = fechaCorta(c.fecha);
                  const que = c.accion || c.asunto || '';
                  return (
                    <li key={c.id} className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2">
                      <span className="text-sm text-amber-900 min-w-0 flex-1 truncate" title={`${quien}${fecha ? `, ${fecha}` : ''}: ${que}`}>
                        <span className="font-medium">{quien}{fecha ? `, ${fecha}` : ''}:</span> {que}
                      </span>
                      <span className="flex items-center gap-1.5 shrink-0">
                        <button
                          type="button"
                          onClick={() => abrir(c.id)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-amber-400 bg-white text-xs text-amber-800 hover:bg-amber-100"
                          data-testid={`factura-correo-abrir-${c.id}`}
                        >
                          <ExternalLink className="w-3 h-3" /> Abrir
                        </button>
                        <button
                          type="button"
                          onClick={() => marcarGestionado(c.id)}
                          disabled={marcando === c.id}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-emerald-300 bg-white text-xs text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                          title="Dar por gestionado en el ERP (no toca el buzón)"
                          data-testid={`factura-correo-gestionado-${c.id}`}
                        >
                          <CheckCircle2 className="w-3 h-3" /> Gestionado
                        </button>
                      </span>
                    </li>
                  );
                })}
              </ul>
              {error && <p className="text-xs text-red-700 mt-1">{error}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
